import React from 'react';

type SetPendingEdit = (key: string, pending: boolean) => void;

const PendingEditsContext = React.createContext<SetPendingEdit>(() => {});

export const PendingEditsProvider = PendingEditsContext.Provider;

/**
 * Tracks the editors that hold debounced input which has not reached Formik
 * yet, so a meeting refetch can hold its reset until they are idle.
 */
export const usePendingEditsTracker = () => {
  const [pendingKeys, setPendingKeys] = React.useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const setPending = React.useCallback<SetPendingEdit>((key, pending) => {
    setPendingKeys((prev) => {
      if (pending === prev.has(key)) return prev;
      const next = new Set(prev);
      if (pending) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);

  return { hasPendingEdits: pendingKeys.size > 0, setPending };
};

/** Registers this editor as holding un-flushed input while `pending` is true. */
export const usePendingEdit = (key: string, pending: boolean) => {
  const setPending = React.useContext(PendingEditsContext);
  React.useEffect(() => {
    setPending(key, pending);
    return () => setPending(key, false);
  }, [key, pending, setPending]);
};
